import { GridApi } from 'ag-grid-community';

export const downloadGridDataAsCSV = (gridApi: GridApi | undefined, filename: string) => {
    if (!gridApi) return;

    gridApi.exportDataAsCsv({
        fileName: filename
    });
};

export const downloadRoyaltyVersionsCSV = async (
    executionRequestId: number,
    contractConfigurationClient: any,
    metadata: any,
    playTypeMapping: any,
    convertToUSD = false,
    conversionRates: Map<string, Map<string, number>> = new Map(),
    rawMode = false
) => {
    const response = await contractConfigurationClient.getRoyaltyVersions({ executionRequestId }).toPromise();

    const csvHeaders = rawMode
        ? [
              'Service',
              'Territory',
              'Entity',
              'Month',
              'Entity Vendor Code ID',
              'Royalty Vendor Code',
              'Catalog Vendor Code',
              'Royalty Model Code',
              'Paper Plan Name',
              'Play Type',
              'Program Type Name',
              'Rate Value',
              'Rate Card Currency',
              'Rate Card Amount',
              'Settlement Currency',
              'Settlement Amount'
          ]
        : [
              'Service',
              'Territory',
              'Entity',
              'Month',
              'Entity Vendor Code ID',
              'Royalty Vendor Code',
              'Catalog Vendor Code',
              'Royalty Model Code',
              'Paper Plan Name',
              'Play Type ID',
              'Program Type Name',
              'Prior Rate Value',
              'Current Rate Value',
              'Prior Rate Card Currency',
              'Current Rate Card Currency',
              'Prior Rate Card Amount',
              'Current Rate Card Amount',
              'Prior Settlement Currency',
              'Current Settlement Currency',
              'Prior Settlement Amount',
              'Current Settlement Amount',
              'Prior Version ID',
              'Current Version ID'
          ];

    const entityIds = Array.from(new Set(response.royaltyVersions.map((v: any) => v.entityId)));
    const entitiesResponse = await contractConfigurationClient.getEntitiesByIds({ entityIds }).toPromise();

    const entityIdMapping: { [key: string]: any } = {};
    entitiesResponse.entities?.forEach((entity: any) => {
        entityIdMapping[entity.entityId.toString()] = {
            entity: entity.entityName
        };
    });

    const convertAmount = (amount: number, currency: string, month: string): { amount: string; currency: string } => {
        if (!convertToUSD || currency === 'USD' || !month) {
            return { amount: amount.toFixed(2), currency };
        }
        const monthRates = conversionRates.get(month);
        const rate = monthRates?.get(currency);
        if (!rate) {
            return { amount: amount.toFixed(2), currency };
        }
        return { amount: (amount * rate).toFixed(2), currency: 'USD' };
    };

    const csvRows: string[][] = [];
    const groupedData = new Map<string, any[]>();

    response.royaltyVersions.forEach((version: any) => {
        version.royaltyVersionEntityCodes.forEach((entityCode: any) => {
            entityCode.royaltyVersionData.forEach((data: any) => {
                const groupKey = [
                    version.serviceId,
                    version.territoryId,
                    version.entityId,
                    version.month,
                    entityCode.entityVendorCodeId,
                    data.royaltyVendorCode,
                    data.catalogVendorCode,
                    data.royaltyModelCode,
                    data.territoryCode,
                    data.paperPlanName,
                    data.playTypeId,
                    data.programTypeName
                ].join('|');

                if (!groupedData.has(groupKey)) {
                    groupedData.set(groupKey, []);
                }
                groupedData.get(groupKey)!.push({
                    ...data,
                    serviceId: version.serviceId,
                    territoryId: version.territoryId,
                    entityId: version.entityId,
                    month: version.month,
                    entityVendorCodeId: entityCode.entityVendorCodeId
                });
            });
        });
    });

    groupedData.forEach((versions) => {
        if (rawMode) {
            // Raw mode: only show latest version
            versions.sort((a, b) => a.versionId - b.versionId);
            const latest = versions[versions.length - 1];
            csvRows.push([
                metadata.serviceMapping[latest.serviceId]?.serviceName || latest.serviceId.toString(),
                latest.territoryCode,
                entityIdMapping[latest.entityId.toString()]?.entity || latest.entityId.toString(),
                latest.month,
                latest.entityVendorCodeId.toString(),
                latest.royaltyVendorCode,
                latest.catalogVendorCode,
                latest.royaltyModelCode,
                latest.paperPlanName,
                playTypeMapping[latest.playTypeId] || latest.playTypeId.toString(),
                latest.programTypeName,
                latest.rateValue,
                ...Object.values(convertAmount(latest.rateCardAmount, latest.rateCardCurrency, latest.month)),
                ...Object.values(convertAmount(latest.settlementAmount, latest.settlementCurrency, latest.month))
            ]);
        } else if (versions.length >= 2) {
            versions.sort((a, b) => a.versionId - b.versionId);
            const before = versions[0];
            const after = versions[versions.length - 1];

            const beforeRateCard = convertAmount(before.rateCardAmount, before.rateCardCurrency, before.month);
            const afterRateCard = convertAmount(after.rateCardAmount, after.rateCardCurrency, after.month);
            const beforeSettlement = convertAmount(before.settlementAmount, before.settlementCurrency, before.month);
            const afterSettlement = convertAmount(after.settlementAmount, after.settlementCurrency, after.month);

            csvRows.push([
                metadata.serviceMapping[before.serviceId]?.serviceName || before.serviceId.toString(),
                before.territoryCode,
                entityIdMapping[before.entityId.toString()]?.entity || before.entityId.toString(),
                before.month,
                before.entityVendorCodeId.toString(),
                before.royaltyVendorCode,
                before.catalogVendorCode,
                before.royaltyModelCode,
                before.paperPlanName,
                playTypeMapping[before.playTypeId] || before.playTypeId.toString(),
                before.programTypeName,
                before.rateValue,
                after.rateValue,
                beforeRateCard.currency,
                afterRateCard.currency,
                beforeRateCard.amount,
                afterRateCard.amount,
                beforeSettlement.currency,
                afterSettlement.currency,
                beforeSettlement.amount,
                afterSettlement.amount,
                before.versionId.toString(),
                after.versionId.toString()
            ]);
        } else if (versions.length === 1) {
            const version = versions[0];
            const rateCard = convertAmount(version.rateCardAmount, version.rateCardCurrency, version.month);
            const settlement = convertAmount(version.settlementAmount, version.settlementCurrency, version.month);

            csvRows.push([
                metadata.serviceMapping[version.serviceId]?.serviceName || version.serviceId.toString(),
                version.territoryCode,
                entityIdMapping[version.entityId.toString()]?.entity || version.entityId.toString(),
                version.month,
                version.entityVendorCodeId.toString(),
                version.royaltyVendorCode,
                version.catalogVendorCode,
                version.royaltyModelCode,
                version.paperPlanName,
                playTypeMapping[version.playTypeId] || version.playTypeId.toString(),
                version.programTypeName,
                '',
                version.rateValue,
                '',
                rateCard.currency,
                '',
                rateCard.amount,
                '',
                settlement.currency,
                '',
                settlement.amount,
                '',
                version.versionId.toString()
            ]);
        }
    });

    const csvContent = [csvHeaders.join(','), ...csvRows.map((row) => row.map((field) => `"${field}"`).join(','))].join(
        '\n'
    );

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const filename = `royalty-versions-${rawMode ? 'raw' : 'comparison'}-${executionRequestId}${convertToUSD ? '-USD' : ''}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
